import { ApiError, type ApiErrorDetail } from '../../app/api/client';

export const HOSE_KEY_MAX_LENGTH = 100;
export const HOSE_STRING_MAX_LENGTH = 200;
export const HOSE_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/u;

export type HoseFieldName =
  | 'availableCount'
  | 'insideDiameterM'
  | 'key'
  | 'model'
  | 'name'
  | 'roughnessM'
  | 'standardLength'
  | 'workingPressure';

export const HOSE_FIELD_ORDER: readonly HoseFieldName[] = [
  'name',
  'key',
  'model',
  'insideDiameterM',
  'standardLength',
  'availableCount',
  'workingPressure',
  'roughnessM',
];

export interface HoseDraftValidationInput {
  availableCount: string;
  insideDiameterM: string;
  key: string;
  model: string;
  name: string;
  roughnessM: string;
  standardLength: string;
  workingPressure: string;
}

export interface HoseErrorState {
  correlationId: string | null;
  fieldErrors: Partial<Record<HoseFieldName, string>>;
  summaryMessages: string[];
}

interface HoseIssue {
  field: HoseFieldName | null;
  message: string;
}

const FIELD_LABELS: Record<HoseFieldName, string> = {
  availableCount: 'Available count',
  insideDiameterM: 'Inside diameter',
  key: 'Hose key',
  model: 'Model',
  name: 'Name',
  roughnessM: 'Darcy roughness',
  standardLength: 'Standard length',
  workingPressure: 'Working pressure',
};

const BACKEND_FIELD_MAP: Record<string, HoseFieldName | undefined> = {
  available_count: 'availableCount',
  darcy_weisbach_roughness_m: 'roughnessM',
  inside_diameter_m: 'insideDiameterM',
  key: 'key',
  model: 'model',
  name: 'name',
  nominal_diameter_m: 'insideDiameterM',
  standard_length_m: 'standardLength',
  working_pressure_gauge_pa: 'workingPressure',
};

const KEY_FORMAT_MESSAGE =
  'Use letters, numbers, periods, underscores, or hyphens, beginning with a letter or number.';

function toErrorState(
  issues: readonly HoseIssue[],
  correlationId: string | null = null,
): HoseErrorState | null {
  if (issues.length === 0) return null;

  const fieldErrors: Partial<Record<HoseFieldName, string>> = {};
  for (const issue of issues) {
    if (issue.field !== null && fieldErrors[issue.field] === undefined) {
      fieldErrors[issue.field] = issue.message;
    }
  }

  return {
    correlationId,
    fieldErrors,
    summaryMessages: issues.map((issue) =>
      issue.field === null
        ? issue.message
        : `${FIELD_LABELS[issue.field]}: ${issue.message}`,
    ),
  };
}

function requiredTextIssue(
  field: 'key' | 'model' | 'name',
  value: string,
  maximumLength: number,
): HoseIssue | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return { field, message: 'Enter a value.' };
  }
  if ([...trimmed].length > maximumLength) {
    return {
      field,
      message: `Use ${maximumLength} characters or fewer.`,
    };
  }
  return null;
}

function numericIssue(
  field: HoseFieldName,
  value: string,
  options: { integer?: boolean; positive: boolean },
): HoseIssue | null {
  if (value.trim().length === 0) {
    return { field, message: 'Enter a value.' };
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return { field, message: 'Enter a finite number.' };
  }
  if (options.integer === true && !Number.isInteger(parsed)) {
    return { field, message: 'Enter a whole number.' };
  }
  if (options.positive ? parsed <= 0 : parsed < 0) {
    return {
      field,
      message: options.positive
        ? 'Enter a value greater than 0.'
        : 'Enter 0 or a greater value.',
    };
  }
  return null;
}

export function suggestHoseKey(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/gu, '')
    .replace(/[^A-Za-z0-9]+/gu, '-')
    .replace(/^-+|-+$/gu, '')
    .toUpperCase()
    .slice(0, HOSE_KEY_MAX_LENGTH);
}

export function validateHoseDraft(
  draft: HoseDraftValidationInput,
): HoseErrorState | null {
  const issues: HoseIssue[] = [];
  const keyIssue = requiredTextIssue('key', draft.key, HOSE_KEY_MAX_LENGTH);
  if (keyIssue !== null) {
    issues.push(keyIssue);
  } else if (!HOSE_KEY_PATTERN.test(draft.key.trim())) {
    issues.push({ field: 'key', message: KEY_FORMAT_MESSAGE });
  }

  for (const issue of [
    requiredTextIssue('name', draft.name, HOSE_STRING_MAX_LENGTH),
    requiredTextIssue('model', draft.model, HOSE_STRING_MAX_LENGTH),
    numericIssue('insideDiameterM', draft.insideDiameterM, { positive: true }),
    numericIssue('standardLength', draft.standardLength, { positive: true }),
    numericIssue('availableCount', draft.availableCount, {
      integer: true,
      positive: false,
    }),
    numericIssue('workingPressure', draft.workingPressure, { positive: true }),
    numericIssue('roughnessM', draft.roughnessM, { positive: false }),
  ]) {
    if (issue !== null) issues.push(issue);
  }

  return toErrorState(issues);
}

function fieldFromDetail(detail: ApiErrorDetail): HoseFieldName | null {
  for (let index = detail.location.length - 1; index >= 0; index -= 1) {
    const part = detail.location[index];
    if (typeof part === 'string') {
      const field = BACKEND_FIELD_MAP[part];
      if (field !== undefined) return field;
    }
  }
  return null;
}

function apiDetailMessage(
  detail: ApiErrorDetail,
  field: HoseFieldName | null,
): string {
  if (field === 'key' && detail.issue_type === 'unique') {
    return 'This hose key is already in use. Choose a different key.';
  }
  if (field === 'key' && detail.issue_type === 'string_pattern_mismatch') {
    return KEY_FORMAT_MESSAGE;
  }
  if (detail.issue_type === 'string_too_long') {
    return field === 'key'
      ? `Use ${HOSE_KEY_MAX_LENGTH} characters or fewer.`
      : `Use ${HOSE_STRING_MAX_LENGTH} characters or fewer.`;
  }
  if (detail.issue_type === 'greater_than') {
    return 'Enter a value greater than 0.';
  }
  if (detail.issue_type === 'greater_than_equal') {
    return 'Enter 0 or a greater value.';
  }
  return detail.reason;
}

export function hoseErrorFromUnknown(error: unknown): HoseErrorState {
  if (error instanceof ApiError) {
    const issues =
      error.details?.map((detail) => {
        const field = fieldFromDetail(detail);
        return {
          field,
          message: apiDetailMessage(detail, field),
        };
      }) ?? [];
    const mapped = toErrorState(
      issues.length > 0
        ? issues
        : [{ field: null, message: error.message }],
      error.correlationId,
    );
    if (mapped !== null) return mapped;
  }

  return {
    correlationId: null,
    fieldErrors: {},
    summaryMessages: [
      'The hose type could not be saved. Keep this draft and try again.',
    ],
  };
}
